import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';

import { authService } from '@/services/auth';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AuthBackground } from '@/components/AuthBackground';
import { useAppErrorToast } from '@/hooks/use-app-error-toast';

const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'E-mail é obrigatório').email('E-mail inválido'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showErrorToast } = useAppErrorToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onChange',
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsSubmitting(true);
    try {
      await authService.forgotPassword({ email: data.email });
    } catch (error: unknown) {
      showErrorToast(error, {
        title: 'Erro ao enviar e-mail',
        fallbackMessage: 'Tente novamente mais tarde.',
      });
      return;
    } finally {
      setIsSubmitting(false);
    }
    navigate('/forgot-password/sent', { state: { email: data.email } });
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-highlight flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative">
      <AuthBackground />
      <div className="relative z-10">
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-8 w-full sm:w-96 flex flex-col gap-6">
          <div className="text-center">
            <h1 className="text-xl font-sans font-semibold text-gray-900 mb-2">
              Esqueceu sua senha?
            </h1>
            <p className="text-sm font-sans font-normal text-[#737373]">
              Digite o e-mail cadastrado na sua conta e enviaremos um link para você criar uma nova senha.
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-1">
                E-mail
              </label>
              <Input
                id="email"
                type="email"
                {...register('email')}
                placeholder="Digite seu e-mail"
                error={errors.email?.message}
              />
            </div>
            <Button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="w-full bg-black text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {isSubmitting ? 'Enviando...' : 'Redefinir senha'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
